<template>
    <div class="date-picker">
        <fieldset class="date-picker__monthyear">
            <div class="picker-row">
                <button v-on:click.stop="month--" class="btn-change-month prev">
                    &blacktriangleleft;
                </button>
                <div class="form-group">
                    <select
                        name="months"
                        class="date-picker__month form-control"
                        id="pickerMonth"
                        v-model="month"
                        v-on:click.stop
                        @keyup.up="month = month + 1"
                        @keyup.down="month = month - 1"
                        @keyup.right="month = month + 1"
                        @keyup.left="month = month - 1"
                    >
                        <option
                            v-for="n in 12"
                            :key="'month-' + n"
                            :value="n - 1"
                        >
                            {{
                                dayjs()
                                    .month(n - 1)
                                    .format('MMMM')
                            }}
                        </option>
                    </select>
                </div>
                <div class="form-group">
                    <select
                        name="year"
                        class="date-picker__year form-control"
                        id="pickerYear"
                        v-model="year"
                        v-on:click.stop
                        @keyup.up="year = year + 1"
                        @keyup.down="year = year - 1"
                        @keyup.right="year = year + 1"
                        @keyup.left="year = year - 1"
                    >
                        <option
                            v-for="year in years"
                            :key="'year-' + year"
                            :value="year"
                            >{{ year }}</option
                        >
                    </select>
                </div>
                <button v-on:click.stop="month++" class="btn-change-month next">
                    &blacktriangleright;
                </button>
            </div>
        </fieldset>
        <div class="calendar-container">
            <div
                class="calendar-day"
                v-for="day in calendarHeaders"
                :key="'header-' + day"
            >
                <div class="day weekday">{{ day }}</div>
            </div>
            <button
                v-for="(dayObj, index) in days"
                :key="'calendar-day-' + index"
                :class="{
                    'calendar-day': true,
                    'in-past': dayObj.isBefore(dayjs()),
                    'prev-month': dayObj.isBefore(dayjs(), 'month'),
                    today: dayObj.isSame(dayjs(), 'date'),
                    'start-day': dayObj.isSame(startDate, 'date'),
                    selected: value
                        ? dayObj.isSame(dayjs(value), 'date')
                        : false,
                }"
                @focus.prevent="changeDay('set', dayObj)"
                @keyup.left="changeDay('subtract', 1)"
                @keyup.right="changeDay('add', 1)"
                @keyup.up="changeDay('subtract', 7)"
                @keyup.down="changeDay('add', 7)"
                @click="
                    $emit('input', dayObj.toDate());
                    $emit('close');
                "
                @keyup.enter="
                    $emit('input', dayObj.toDate());
                    $emit('close');
                "
                :aria-label="'Select ' + dayObj.format('MMMM D, YYYY')"
                :data-day="dayObj"
            >
                <div class="day">{{ dayObj.date() }}</div>
            </button>
        </div>
    </div>
</template>

<script>
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);

export default {
    name: 'datePicker',
    props: {
        startDate: {
            // type: [Object, String, Boolean, Date, null],
            default: null,
        },
        value: {
            // type: [Object, String, Date],
            default: null,
        },
        numYearsPastToShow: {
            // type: Number,
            default: 0,
        },
        numYearsFutureToShow: {
            // type: Number,
            default: 5,
        },
        calendarHeaders: {
            // type: Array,
            default: () => {
                return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            },
            description: 'The headers row values for the calendar.',
        },
    },
    data() {
        return {
            dayval: {
                current: null,
                prev: null,
            },
            monthval: {
                current: null,
                prev: null,
            },
            yearval: {
                current: null,
                prev: null,
            },
            pickerHover: null,
            days: [],
        };
    },
    computed: {
        day: {
            get() {
                return this.dayval.current;
            },
            set(val) {
                this.dayval.prev = this.dayval.current;
                this.dayval.current = val;

                if (window.queuedUpdate) {
                    window.clearInterval(window.queuedUpdate);
                }

                window.queuedUpdate = window.setTimeout(
                    this.updateDays,
                    0,
                    true
                );
                window.setTimeout(this.refocus, 0);
            },
        },
        month: {
            get() {
                return this.monthval.current;
            },
            set(val) {
                let needsUpdate = false;
                if (val != this.monthval.prev) needsUpdate = true;
                this.monthval.prev = this.monthval.current;
                this.monthval.current = val;

                if (!needsUpdate) return;

                if (window.queuedUpdate) {
                    window.clearInterval(window.queuedUpdate);
                }

                window.queuedUpdate = window.setTimeout(
                    this.updateDays,
                    0,
                    true
                );
                window.setTimeout(this.refocus, 0);
            },
        },
        year: {
            get() {
                return this.yearval.current;
            },
            set(val) {
                let needsUpdate = false;
                if (val != this.yearval.prev) needsUpdate = true;
                this.yearval.prev = this.yearval.current;
                this.yearval.current = val;

                // If nothing changed, we don't need an update
                if (!needsUpdate) return;

                // Debounce updates so we don't trigger more than we need
                if (window.queuedUpdate) {
                    window.clearInterval(window.queuedUpdate);
                }

                window.queuedUpdate = window.setTimeout(
                    this.updateDays,
                    0,
                    true
                );
                window.setTimeout(this.refocus, 0);
            },
        },
        years() {
            let years = [];
            const thisYear = dayjs().year();
            for (
                let i = 0 - this.numYearsPastToShow;
                i <= this.numYearsFutureToShow;
                i++
            ) {
                years.push(thisYear + i);
            }
            return years;
        },
        selectedDay: {
            get: function() {
                return dayjs(new Date(this.year, this.month, this.day));
            },
            set: function(newDay) {
                const dateStamp = newDay.format('YYYY-MM-DD');

                this.day = newDay.date();
                this.month = newDay.month();
                this.year = newDay.year();

                const newFocusedDay = document.querySelector(
                    'button[data-day="' + dateStamp + '"'
                );

                if (newFocusedDay) newFocusedDay.focus();
            },
        },
        triggerButton() {
            return this.$parent.$el.querySelector('.filter-trigger');
        },
        parentInput() {
            return this.$parent.$el.querySelector('input');
        },
        pickerDateObj() {
            return dayjs(new Date(this.year, this.month, this.day));
        },
    },
    methods: {
        dayjs: dayjs,
        updateDays() {
            const rootDate = dayjs(new Date(this.year, this.month, 1));
            let days = [];

            for (let i = 0 - rootDate.day(); i < rootDate.daysInMonth(); i++) {
                days.push(rootDate.add(i, 'day'));
            }

            this.days = days;
        },
        refocus() {
            const newFocus = document.querySelector(
                'button[data-day="' +
                    this.pickerDateObj.format('YYYY-MM-DD') +
                    '"'
            );
            if (newFocus) newFocus.focus();
        },
        changeDay(op, val) {
            var newDate = dayjs(new Date(this.year, this.month, this.day));
            switch (op) {
                case 'add': {
                    newDate = newDate.add(parseInt(val), 'day');
                    break;
                }

                case 'subtract': {
                    newDate = newDate.subtract(parseInt(val), 'day');
                    break;
                }

                case 'set': {
                    newDate = val;
                    break;
                }

                default: {
                    break;
                }
            }

            this.year = newDate.year();
            this.month = newDate.month();
            this.day = newDate.date();
        },
        positionPicker(relativeEl) {
            const el = this.$el;
            if (process.env.NODE_ENV !== 'production') {
                console.log(relativeEl);
            }
            el.classList.remove('picker-top');
            el.classList.remove('picker-bottom');

            const { height: pickerHeight } = el.getBoundingClientRect();
            const {
                // height: inputHeight,
                top: inputTop,
            } = relativeEl.getBoundingClientRect();
            const roomAbove = inputTop > pickerHeight ? true : false;
            if (roomAbove) {
                el.classList.add('picker-top');
                // el.style.top = `-${pickerHeight + 16}px`;
            } else {
                el.classList.add('picker-bottom');
                // el.style.top = `${inputHeight + 16}px`;
            }
        },
        pickerKeyNavListener(e) {
            switch (e.keyCode) {
                case 37:
                case 38:
                case 39:
                case 40: {
                    e.preventDefault();
                    break;
                }
                case 27: {
                    this.$emit('close');
                    break;
                }
                default: {
                    return;
                }
            }
        },
    },
    beforeMount() {
        // set the picker info to either the values in the field or, if it's empty, the values of today
        // We assume that default dates provided are in the format YYYY-MM-DD
        this.day =
            dayjs(this.value, 'YYYY-MM-DD').date() ||
            dayjs(this.startDate, 'YYYY-MM-DD').date() ||
            dayjs().date();
        this.month =
            dayjs(this.value, 'YYYY-MM-DD').month() ||
            dayjs(this.startDate, 'YYYY-MM-DD').month() ||
            dayjs().month();
        this.year =
            dayjs(this.value, 'YYYY-MM-DD').year() ||
            dayjs(this.startDate, 'YYYY-MM-DD').year() ||
            dayjs().year();

        window.addEventListener('keydown', this.pickerKeyNavListener);
    },
    mounted() {
        if (this.triggerButton) this.positionPicker(this.triggerButton);
    },
    beforeDestroy() {
        window.removeEventListener('keydown', this.pickerKeyNavListener);
    },
};
</script>

<style lang="scss">
.date-picker {
    display: flex;
    flex-flow: column nowrap;
    justify-content: center;
    align-items: flex-start;

    label {
        align-self: flex-start;
    }

    .modal-header {
        display: flex;
        flex-flow: row nowrap;
        justify-content: space-between;
        align-items: center;
        margin: 0;
        padding: 20px;
        border-bottom: 1px solid #b9b9b9;

        h5 {
            margin: 0;
            padding: 0;
            font-weight: 300;
        }

        .modal-close {
            display: inline-flex;
            flex-flow: row nowrap;
            align-items: center;
            padding: 5px;
            margin: 0;
            line-height: 0;
            background: white;
            border: none;
            border-radius: 3.5px;
            font-size: 16px;
            line-height: 15px;

            &:hover {
                background: #e2e2e2;
            }

            &:after {
                content: '\2715';
                display: inline-block;
                font-size: 18px;
                height: 15px;
                width: 15px;
                margin: 0;
                margin-left: 10px;
                padding: 0;
            }
        }
    }

    .picker-row {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 300px;

        // select{
        //     margin: 0 2px;
        // }
        button {
            margin: 0 2px;
        }
    }

    .btn-change-month {
        background-color: #fff;
        -webkit-appearance: none;
        border: none;
        font-size: 16px;
        color: rgb(52, 52, 52);
    }

    .date-picker__calendar-container,
    .calendar-container {
        margin: 5px 10px;
        max-width: 100%; //IE11 fix

        .calendar-day {
            display: inline-flex;
            width: 14.2857%;
            max-width: 14.2857%;
            justify-content: center;
            align-items: left;
            float: left;
            padding: 15px;
            cursor: pointer;
            border: 1px solid transparent;
            background-color: transparent;

            &:hover,
            &:focus {
                background-color: #f0f0f0;
                border-radius: 25px;
                border-color: #6e6e6e;
                box-shadow: none;
                outline: none;
            }

            &:hover .day,
            &:focus .day {
                box-shadow: none;
                outline: none;
            }

            .day {
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 16px;
                line-height: 16px;
                width: 10px;
                height: 10px;
                border: none;
                padding: 0;
                background-color: transparent;

                &.weekday {
                    text-align: center;
                }
            }

            &.prev-month {
                color: #7f7f7f;
            }

            &.today {
                font-weight: bold;
            }

            &.start-day {
                // border-radius: 25px 0 0 25px;
                border-radius: 25px;
                background: rgba(0, 128, 0, 0.605);

                &:hover,
                &:focus {
                    background-color: rgba(0, 175, 0, 0.308);
                    box-shadow: none;
                    outline: none;
                }
            }

            &.selected {
                background: #5bc2e7;
            }

            &.timespan {
                background: rgba(0, 128, 0, 0.605);

                &.endspan {
                    border-radius: 0 25px 25px 0;
                }
            }
        }
    }
}
</style>
